/**
 * Modular, data-gated curb-weight page builder.
 *
 * Every module below is emitted only when this specific vehicle has the data
 * that module reads. Nothing is templated as "not available" — a missing input
 * means the module simply does not exist on that page, so two vehicles with
 * different records produce structurally different pages.
 *
 * Every sentence is computed from stored rows: NHTSA Canadian Vehicle
 * Specifications (dimensions, weight distribution, track), fueleconomy.gov
 * (EPA powertrain list and combined MPG) and manufacturer-published
 * specification rows (towing, fuel tank). No figure is estimated or carried in
 * from outside those records, except the two clearly-labelled physical
 * constants used in the fuel-load calculation.
 */

/**
 * Where one model sits inside its EPA size class by curb weight, computed by
 * the `curb_weight_class_position` SQL function over the whole database.
 */
export interface ClassPosition {
  vehicleClass: string;
  peerCount: number;
  medianLbs: number;
  rank: number;
  modelLbs: number;
}

/**
 * The rebuilt layout now applies to every curb-weight page. It is fully
 * data-gated: `buildCurbWeightPage` returns null when a vehicle has no
 * recorded weights, and the route falls back to the previous rendering then.
 */
export function isRebuiltCurbWeightPage(
  _makeSlug: string,
  _modelSlug: string,
  dataType: string,
): boolean {
  return dataType === "curb-weight";
}

export interface RebuildDim {
  year: number;
  variant_label: string | null;
  curb_weight_lbs: number | null;
  length_in: number | null;
  width_in: number | null;
  height_in: number | null;
  wheelbase_in: number | null;
  track_front_in: number | null;
  track_rear_in: number | null;
  weight_distribution: string | null;
  source?: string | null;
  verified_at?: string | null;
}

export interface RebuildVehicle {
  year: number;
  trim: string | null;
  drive: string | null;
  displacement: number | null;
  cylinders: number | null;
  atv_type: string | null;
  fuel_type: string | null;
  mpg_combined: number | null;
  vehicle_class: string | null;
}

export interface RebuildSpec {
  year: number;
  spec_type: string;
  towing_capacity_lbs: number | null;
  towing_config: string | null;
  payload_capacity_lbs: number | null;
  gcwr_lbs: number | null;
  fuel_tank_gal: number | null;
  source?: string | null;
  verified_at?: string | null;
}

export interface CurbWeightModule {
  id: string;
  heading: string;
  /** Sort key: the number of pounds the module explains. Larger sorts first. */
  magnitude: number;
  paragraphs: string[];
  rows?: { label: string; value: string; note?: string | undefined }[];
}

export interface CurbWeightFaq {
  q: string;
  a: string;
}

export interface CurbWeightPage {
  h1: string;
  answer: string;
  /** Snippet-length version of the answer, for <title>/description use. */
  shortAnswer: string;
  modules: CurbWeightModule[];
  faqs: CurbWeightFaq[];
  sourceLine: string;
}

export interface BuildInput {
  makeName: string;
  modelName: string;
  fullName: string;
  dimensions: RebuildDim[];
  vehicles: RebuildVehicle[];
  specs: RebuildSpec[];
  classPosition: ClassPosition | null;
  /** Unit-aware weight formatter supplied by the caller. */
  fmt: (lbs: number) => string;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

const r = (n: number) => Math.round(n);
const n0 = (n: number) => r(n).toLocaleString("en-US");

/** US gallon of gasoline ≈ 6.1 lb (US DOE fuel property data). */
const LB_PER_GALLON = 6.1;

interface Variant {
  row: RebuildDim;
  lbs: number;
  label: string;
  body: string;
  drive: "4x4" | "2WD" | null;
  hybrid: boolean;
}

const DRIVE_4 = /\b(4X4|4WD|AWD)\b/;
const DRIVE_2 = /\b(2WD|RWD|FWD)\b/;

function tidy(label: string, modelName: string): string {
  let s = label.toUpperCase();
  for (const token of modelName.toUpperCase().split(/\s+/)) {
    s = s.replace(new RegExp(`\\b${token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g"), " ");
  }
  s = s.replace(/\bP\/U\b/g, " ").replace(/\s+/g, " ").trim();
  if (!s) return label;
  return s
    .split(" ")
    .map((w) => {
      if (/^(4X4|2WD|4WD|AWD|RWD|FWD|SUV|R)$/.test(w)) return w === "4X4" ? "4x4" : w;
      if (/^\d/.test(w)) return w.replace("FT", "ft").replace("DR", "dr");
      const t = w.charAt(0) + w.slice(1).toLowerCase();
      return t.replace(/^Supercrew$/, "SuperCrew").replace(/^Supercab$/, "SuperCab");
    })
    .join(" ");
}

function toVariants(rows: RebuildDim[], modelName: string): Variant[] {
  return rows
    .filter((d) => d.curb_weight_lbs != null && d.variant_label)
    .map((d) => {
      const raw = (d.variant_label as string).toUpperCase();
      const drive = DRIVE_4.test(raw) ? "4x4" : DRIVE_2.test(raw) ? "2WD" : null;
      const hybrid = /HYBRID/.test(raw);
      const body = raw
        .replace(DRIVE_4, " ")
        .replace(DRIVE_2, " ")
        .replace(/HYBRID/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      return {
        row: d,
        lbs: d.curb_weight_lbs as number,
        label: tidy(d.variant_label as string, modelName),
        body,
        drive: drive as Variant["drive"],
        hybrid,
      };
    });
}

function powertrainLabel(v: RebuildVehicle): string {
  const parts: string[] = [];
  const disp = v.displacement == null ? null : Number(v.displacement);
  if (disp) parts.push(`${disp.toFixed(1)}L`);
  if (v.cylinders) parts.push(`${v.cylinders}-cyl`);
  if (v.atv_type) parts.push(v.atv_type.toLowerCase());
  if (v.drive) {
    const d = v.drive
      .replace(/Part-time 4-Wheel Drive|4-Wheel Drive|Four-Wheel Drive/i, "4WD")
      .replace(/All-Wheel Drive/i, "AWD")
      .replace(/Rear-Wheel Drive/i, "RWD")
      .replace(/Front-Wheel Drive/i, "FWD");
    parts.push(d);
  }
  return parts.join(" ");
}

// ---------------------------------------------------------------------------
// builder
// ---------------------------------------------------------------------------

export function buildCurbWeightPage(input: BuildInput): CurbWeightPage | null {
  const { fullName, modelName, dimensions, vehicles, specs, classPosition, fmt } = input;
  const weighed = dimensions.filter((d) => d.curb_weight_lbs != null);
  if (weighed.length === 0) return null;

  const latestYear = Math.max(...weighed.map((d) => d.year));
  const latest = weighed.filter((d) => d.year === latestYear);
  const variants = toVariants(latest, modelName);
  if (variants.length === 0) return null;

  const lightest = variants.reduce((b, v) => (v.lbs < b.lbs ? v : b));
  const heaviest = variants.reduce((b, v) => (v.lbs > b.lbs ? v : b));
  const spread = r(heaviest.lbs - lightest.lbs);

  const modules: CurbWeightModule[] = [];
  const faqs: CurbWeightFaq[] = [];

  // --- answer -------------------------------------------------------------
  const single = variants.length === 1 || spread === 0;
  const h1 = single
    ? `${fullName} Curb Weight: ${fmt(r(heaviest.lbs))}`
    : `${fullName} Curb Weight: ${fmt(r(lightest.lbs))} to ${fmt(r(heaviest.lbs))}`;
  const answer = single
    ? `The ${latestYear} ${fullName} is recorded at a single curb weight of ${fmt(r(heaviest.lbs))} — every configuration in the NHTSA file shares that figure.`
    : `The ${latestYear} ${fullName} ranges from ${fmt(r(lightest.lbs))} (${lightest.label}) to ${fmt(r(heaviest.lbs))} (${heaviest.label}), a spread of ${fmt(spread)} across ${variants.length} recorded configurations.`;
  const shortAnswer = single
    ? `${fmt(r(heaviest.lbs))} (${latestYear})`
    : `${fmt(r(lightest.lbs)).replace(/\s+(?:lbs|kg)$/, "")}–${fmt(r(heaviest.lbs))} (${latestYear})`;

  // --- towing headroom ----------------------------------------------------
  const towSpec = specs
    .filter((s) => s.spec_type === "towing-capacity" && s.towing_capacity_lbs)
    .sort((a, b) => b.year - a.year)[0];
  if (towSpec?.towing_capacity_lbs) {
    const tow = towSpec.towing_capacity_lbs;
    const applies = towSpec.year === latestYear;
    const paras = [
      `With a ${towSpec.towing_config ? `${towSpec.towing_config}` : "maximum-rated"} configuration, the ${towSpec.year} ${modelName} is rated to pull ${fmt(tow)} — ${(tow / heaviest.lbs).toFixed(1)} times the ${fmt(r(heaviest.lbs))} the heaviest recorded truck weighs by itself, and ${(tow / lightest.lbs).toFixed(1)} times the ${fmt(r(lightest.lbs))} of the lightest.`,
      `That rating belongs to the ${towSpec.towing_config ?? "maximum tow"} build, not to every truck on this page: a ${heaviest.label} starts ${fmt(r(heaviest.lbs - lightest.lbs))} deeper into the combined rating than a ${lightest.label} before a trailer is coupled.`,
    ];
    if (!applies) {
      paras.push(
        `The towing figure on file is for ${towSpec.year}; the weights above are ${latestYear} records.`,
      );
    }
    modules.push({
      id: "towing",
      heading: `What the weight leaves for towing`,
      magnitude: tow - heaviest.lbs,
      paragraphs: paras,
      rows: [
        { label: "Maximum trailer rating", value: fmt(tow), note: towSpec.towing_config ?? undefined },
        { label: "Heaviest recorded truck", value: fmt(r(heaviest.lbs)), note: heaviest.label },
        { label: "Lightest recorded truck", value: fmt(r(lightest.lbs)), note: lightest.label },
      ],
    });
    faqs.push({
      q: `Does the ${modelName}'s curb weight count against its tow rating?`,
      a: `Yes. The ${fmt(tow)} maximum applies to the ${towSpec.towing_config ?? "maximum tow"} configuration, and the truck's own ${fmt(r(lightest.lbs))}–${fmt(r(heaviest.lbs))} is already inside the combined weight the rating assumes. Heavier cab and drivetrain choices eat into what is left.`,
    });
  }

  // --- drivetrain delta ---------------------------------------------------
  const drivePairs: { body: string; two: Variant; four: Variant; delta: number }[] = [];
  for (const two of variants.filter((v) => v.drive === "2WD" && !v.hybrid)) {
    const four = variants.find((v) => v.drive === "4x4" && !v.hybrid && v.body === two.body);
    if (four) drivePairs.push({ body: two.body, two, four, delta: four.lbs - two.lbs });
  }
  if (drivePairs.length >= 1) {
    const deltas = drivePairs.map((p) => p.delta);
    const lo = r(Math.min(...deltas));
    const hi = r(Math.max(...deltas));
    const avgPct = (
      (drivePairs.reduce((s, p) => s + p.delta / p.two.lbs, 0) / drivePairs.length) *
      100
    ).toFixed(1);
    modules.push({
      id: "drivetrain-delta",
      heading: `What four-wheel drive costs in weight`,
      magnitude: hi,
      paragraphs: [
        lo === hi
          ? `Every body style recorded in both drive layouts gains exactly ${fmt(hi)} moving from two-wheel to four-wheel drive — about ${avgPct}% of the two-wheel-drive figure.`
          : `Across the ${drivePairs.length} body styles recorded in both layouts, four-wheel drive adds between ${fmt(lo)} and ${fmt(hi)} — roughly ${avgPct}% of the two-wheel-drive weight. The transfer case, front differential and driveshaft account for that in every case, so the penalty barely moves with cab size.`,
      ],
      rows: drivePairs.map((p) => ({
        label: tidy(p.two.row.variant_label ?? "", modelName).replace(/\s*2WD$/, ""),
        value: `+${fmt(r(p.delta))}`,
        note: `${fmt(r(p.two.lbs))} → ${fmt(r(p.four.lbs))}`,
      })),
    });
    faqs.push({
      q: `How much more does a 4x4 ${modelName} weigh?`,
      a: lo === hi
        ? `${fmt(hi)} more than the same body style in two-wheel drive.`
        : `Between ${fmt(lo)} and ${fmt(hi)} more than the identical body style in two-wheel drive, depending on cab and box.`,
    });
  }

  // --- powertrain (hybrid) delta -----------------------------------------
  const hybridPairs: { base: Variant; hyb: Variant; delta: number }[] = [];
  for (const hyb of variants.filter((v) => v.hybrid)) {
    const base = variants.find((v) => !v.hybrid && v.body === hyb.body && v.drive === hyb.drive);
    if (base) hybridPairs.push({ base, hyb, delta: hyb.lbs - base.lbs });
  }
  if (hybridPairs.length) {
    const p = hybridPairs.reduce((b, x) => (x.delta > b.delta ? x : b));
    const pct = ((p.delta / p.base.lbs) * 100).toFixed(1);
    const hybridMpg = vehicles
      .filter((v) => v.year === latestYear && v.atv_type && v.mpg_combined)
      .map((v) => v.mpg_combined as number);
    const gasMpg = vehicles
      .filter((v) => v.year === latestYear && !v.atv_type && v.mpg_combined)
      .map((v) => v.mpg_combined as number);
    const paras = [
      `Identical body, ${fmt(r(p.delta))} apart: the ${p.hyb.label} records ${fmt(r(p.hyb.lbs))} against ${fmt(r(p.base.lbs))} for the ${p.base.label}, a ${pct}% increase carried by the battery, the second motor and the heavier transmission case.`,
    ];
    if (hybridMpg.length && gasMpg.length) {
      const hMax = Math.max(...hybridMpg);
      const gMax = Math.max(...gasMpg);
      const gain = hMax - gMax;
      const lbsPerMpg = gain > 0 ? r(p.delta / gain) : 0;
      paras.push(
        gain > 0
          ? `The best EPA combined figure on the hybrid is ${hMax} mpg against ${gMax} mpg on the best conventional engine — ${fmt(r(p.delta))} of extra hardware for ${gain} mpg, or about ${fmt(lbsPerMpg)} per mpg gained.`
          : `EPA's best combined figure is ${hMax} mpg hybrid against ${gMax} mpg conventional, so on the combined cycle the extra ${fmt(r(p.delta))} does not buy a headline economy gain.`,
      );
    }
    modules.push({
      id: "hybrid-delta",
      heading: `What the hybrid hardware adds`,
      magnitude: p.delta,
      paragraphs: paras,
    });
    faqs.push({
      q: `How much heavier is the hybrid ${modelName}?`,
      a: `${fmt(r(p.delta))} — ${fmt(r(p.hyb.lbs))} versus ${fmt(r(p.base.lbs))} for the same body style with the conventional powertrain.`,
    });
  }

  // --- separate chassis (off-road / wide-track variants) ------------------
  const trackValues = variants.map((v) => v.row.track_front_in ?? 0).filter(Boolean);
  if (trackValues.length > 1) {
    const baseTrack = trackValues.sort((a, b) => a - b)[Math.floor(trackValues.length / 2)] as number;
    const wide = variants.filter(
      (v) => v.row.track_front_in && Math.abs(v.row.track_front_in - baseTrack) >= 1.5,
    );
    if (wide.length) {
      const w = wide.reduce((b, v) => (v.lbs > b.lbs ? v : b));
      const ref = variants
        .filter((v) => !wide.includes(v) && v.row.wheelbase_in === w.row.wheelbase_in)
        .reduce<Variant | null>((b, v) => (!b || v.lbs > b.lbs ? v : b), null);
      const trackDelta = (w.row.track_front_in as number) - baseTrack;
      const paras = [
        `The ${w.label} is not a trim level on the same chassis. Its front track measures ${(w.row.track_front_in as number).toFixed(1)} in against ${baseTrack.toFixed(1)} in on the standard body — ${trackDelta.toFixed(1)} in wider — and the body is ${(w.row.width_in ?? 0).toFixed(1)} in across.`,
      ];
      if (ref) {
        paras.push(
          `On the same ${(w.row.wheelbase_in as number).toFixed(1)} in wheelbase it records ${fmt(r(w.lbs))} against ${fmt(r(ref.lbs))} for the ${ref.label}: ${fmt(r(w.lbs - ref.lbs))} of extra suspension, tire and structure.`,
        );
      }
      if (wide.length > 1) {
        const pair = [...wide].sort((a, b) => a.lbs - b.lbs);
        const a = pair[0] as Variant;
        const b = pair[pair.length - 1] as Variant;
        if (b.lbs !== a.lbs) {
          paras.push(
            `Within that wide-track group the spread is ${fmt(r(b.lbs - a.lbs))}: ${a.label} at ${fmt(r(a.lbs))}, ${b.label} at ${fmt(r(b.lbs))}.`,
          );
        }
      }
      modules.push({
        id: "wide-track",
        heading: `The ${w.label} is a different vehicle on the scales`,
        magnitude: ref ? w.lbs - ref.lbs : 0,
        paragraphs: paras,
      });
    }
  }

  // --- fuel load ----------------------------------------------------------
  const tank = specs
    .filter((s) => s.spec_type === "fuel-tank-capacity" && s.fuel_tank_gal)
    .sort((a, b) => b.year - a.year)[0];
  if (tank?.fuel_tank_gal) {
    const fuelLbs = r(tank.fuel_tank_gal * LB_PER_GALLON);
    modules.push({
      id: "fuel-load",
      heading: `Curb weight already includes a full tank`,
      magnitude: fuelLbs,
      paragraphs: [
        `The published ${tank.fuel_tank_gal}-gallon tank holds about ${fmt(fuelLbs)} of gasoline at 6.1 lb per gallon, and curb weight is measured with it full. Run it to empty and the ${heaviest.label} is effectively a ${fmt(r(heaviest.lbs - fuelLbs))} vehicle — ${((fuelLbs / heaviest.lbs) * 100).toFixed(1)}% of the figure quoted above is fuel you will burn off.`,
      ],
    });
  }

  // --- change over time ---------------------------------------------------
  const firstYear = Math.min(...weighed.map((d) => d.year));
  if (firstYear < latestYear) {
    const firstRows = weighed.filter((d) => d.year === firstYear);
    const firstLo = r(Math.min(...firstRows.map((d) => d.curb_weight_lbs as number)));
    const firstHi = r(Math.max(...firstRows.map((d) => d.curb_weight_lbs as number)));
    const loDelta = r(lightest.lbs) - firstLo;
    const hiDelta = r(heaviest.lbs) - firstHi;
    if (loDelta === 0 && hiDelta === 0) {
      modules.push({
        id: "history",
        heading: `Unchanged since ${firstYear}`,
        magnitude: 1,
        paragraphs: [
          `All ${latestYear - firstYear + 1} model years from ${firstYear} to ${latestYear} are filed at ${fmt(r(heaviest.lbs))}, so the model year on a listing does not change the figure.`,
        ],
      });
    } else {
      modules.push({
        id: "history",
        heading: `How the figure moved since ${firstYear}`,
        magnitude: Math.max(Math.abs(loDelta), Math.abs(hiDelta)),
        paragraphs: [
          `The lightest ${firstYear} record is ${fmt(firstLo)}; the lightest ${latestYear} record is ${fmt(r(lightest.lbs))} — ${loDelta === 0 ? "unchanged" : `${fmt(Math.abs(loDelta))} ${loDelta > 0 ? "heavier" : "lighter"} (${((Math.abs(loDelta) / firstLo) * 100).toFixed(1)}%)`}.`,
          hiDelta !== 0
            ? `At the top of the range the ${latestYear} file reaches ${fmt(r(heaviest.lbs))} against ${fmt(firstHi)} in ${firstYear}, a ${fmt(Math.abs(hiDelta))} ${hiDelta > 0 ? "increase" : "reduction"} — driven by which configurations exist, not by the same truck gaining mass.`
            : `The top of the range is identical in both years at ${fmt(firstHi)}.`,
        ],
      });
    }
  }

  // --- class position -----------------------------------------------------
  if (classPosition && classPosition.peerCount >= 10) {
    const { vehicleClass, peerCount, medianLbs, rank, modelLbs } = classPosition;
    const diff = r(modelLbs - medianLbs);
    const lighterThan = peerCount - rank;
    modules.push({
      id: "class",
      heading: `Against the rest of the ${vehicleClass.toLowerCase()} class`,
      magnitude: Math.abs(diff),
      paragraphs: [
        rank <= 3
          ? `${rank === 1 ? `Nothing EPA files under ${vehicleClass} starts lighter than the ${modelName}'s ${fmt(r(modelLbs))}` : `Only ${rank - 1} of the ${peerCount} vehicles EPA files under ${vehicleClass} start lighter than the ${modelName}'s ${fmt(r(modelLbs))}`}, and ${lighterThan} of the ${peerCount} are heavier; the class median is ${fmt(r(medianLbs))}.`
          : lighterThan <= 3
            ? `Just ${lighterThan} of the ${peerCount} ${vehicleClass.toLowerCase()} entries are lighter than the ${modelName}'s ${fmt(r(modelLbs))}, which puts it ${fmt(Math.abs(diff))} ${diff > 0 ? "above" : "below"} the ${fmt(r(medianLbs))} class median.`
            : Math.abs(diff) <= r(medianLbs * 0.03)
              ? `At ${fmt(r(modelLbs))} the ${modelName} sits within ${fmt(Math.abs(diff))} of the ${fmt(r(medianLbs))} median for the ${peerCount} vehicles EPA files under ${vehicleClass}, with ${lighterThan} of them heavier.`
              : `Ranked on the lightest configuration of each nameplate, the ${modelName} is ${rank} of ${peerCount} in ${vehicleClass}, ${fmt(Math.abs(diff))} ${diff > 0 ? "above" : "below"} the ${fmt(r(medianLbs))} median and lighter than ${lighterThan} of its peers.`,
      ],
    });
  }

  // --- axle load ----------------------------------------------------------
  const distRow = [...latest]
    .filter((d) => d.weight_distribution && d.curb_weight_lbs)
    .sort((a, b) => (a.curb_weight_lbs as number) - (b.curb_weight_lbs as number))[0];
  const dist = distRow?.weight_distribution?.match(/^(\d+)\s*\/\s*(\d+)$/);
  const balanced = dist ? Math.abs(Number(dist[1]) - Number(dist[2])) <= 2 : false;
  if (dist && (balanced || variants.length <= 3)) {
    const front = Number(dist[1]) / 100;
    const w = distRow?.curb_weight_lbs as number;
    const frontLbs = r(w * front);
    const rearLbs = r(w - frontLbs);
    const tf = distRow?.track_front_in ?? null;
    const tr = distRow?.track_rear_in ?? null;
    const trackGap = tf && tr ? tf - tr : 0;
    const axleParas = [
      balanced
        ? `NHTSA records a ${dist[1]}/${dist[2]} distribution, so the ${fmt(r(w))} splits to roughly ${fmt(frontLbs)} on the front axle and ${fmt(rearLbs)} on the rear.`
        : `At ${dist[1]}/${dist[2]}, the ${fmt(r(w))} puts about ${fmt(frontLbs)} on the front axle and ${fmt(rearLbs)} on the rear — ${fmt(Math.abs(frontLbs - rearLbs))} more over the front wheels, which is what front tires and front brake pads wear against first.`,
    ];
    if (tf && tr && Math.abs(trackGap) >= 0.2) {
      axleParas.push(
        `The two axles are not the same width either: ${tf.toFixed(1)} in of track at the front against ${tr.toFixed(1)} in at the rear, ${Math.abs(trackGap).toFixed(1)} in ${trackGap > 0 ? "narrower behind" : "narrower in front"}.`,
      );
    }
    modules.push({
      id: "axle",
      heading: balanced ? `Where the mass sits: ${dist[1]}/${dist[2]}` : `Front and rear axle loads`,
      magnitude: Math.abs(frontLbs - rearLbs) || 5,
      paragraphs: axleParas,
    });
    if (balanced) {
      faqs.push({
        q: `How is the ${modelName}'s weight distributed?`,
        a: `${dist[1]}/${dist[2]} front to rear in the NHTSA record — about ${fmt(frontLbs)} on the front axle and ${fmt(rearLbs)} on the rear at a ${fmt(r(w))} curb weight.`,
      });
    }
  }

  // --- data gap -----------------------------------------------------------
  const latestPowertrains = vehicles.filter((v) => v.year === latestYear);
  const distinctPowertrains = new Set(latestPowertrains.map(powertrainLabel).filter(Boolean));
  if (distinctPowertrains.size > variants.length * 2 || (variants.length === 1 && distinctPowertrains.size > 1)) {
    const entryMpg = latestPowertrains
      .map((v) => v.mpg_combined)
      .filter((m): m is number => m != null);
    const mpgClause =
      entryMpg.length > 1 && Math.min(...entryMpg) !== Math.max(...entryMpg)
        ? `, spanning ${Math.min(...entryMpg)}–${Math.max(...entryMpg)} mpg combined`
        : "";
    modules.push({
      id: "gap",
      heading: `What is not in the record`,
      magnitude: 0,
      paragraphs: [
        `EPA files ${latestPowertrains.length} separate ${latestYear} entries for the model${mpgClause}, covering ${distinctPowertrains.size} engine and drive combinations (${[...distinctPowertrains].slice(0, 4).join("; ")}${distinctPowertrains.size > 4 ? "; and others" : ""}) plus their transmission choices, while NHTSA weighs ${variants.length === 1 ? "the body once" : `only ${variants.length} configurations`}. Engine and gearbox choice is therefore not reflected in the figure above.`,
      ],
    });
  }

  // --- source line --------------------------------------------------------
  const dimSource = weighed[0]?.source ?? "NHTSA Canadian Vehicle Specifications";
  const verified = weighed[0]?.verified_at
    ? new Date(weighed[0].verified_at as string).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;
  const extra: string[] = [];
  if (towSpec) extra.push("towing rating from manufacturer-published specifications");
  if (tank) extra.push("fuel-tank capacity from manufacturer-published specifications");
  if (latestPowertrains.length) extra.push("powertrain list and MPG from fueleconomy.gov (US DOE)");
  const sourceLine = `Weights, track and weight distribution from the ${dimSource}${verified ? `, last verified ${verified}` : ""}${extra.length ? `; ${extra.join("; ")}` : ""}.`;

  // FAQ: the headline question, always answerable from the rows above.
  faqs.unshift({
    q: `How much does a ${latestYear} ${fullName} weigh?`,
    a: single
      ? `${fmt(r(heaviest.lbs))} at the curb, the only figure recorded for the ${latestYear} model year.`
      : `Between ${fmt(r(lightest.lbs))} (${lightest.label}) and ${fmt(r(heaviest.lbs))} (${heaviest.label}), depending on the configuration.`,
  });

  modules.sort((a, b) => b.magnitude - a.magnitude);

  return { h1, answer, shortAnswer, modules, faqs: faqs.slice(0, 3), sourceLine };
}
